import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { importMatchesCsv } from "../services/match.service";

const MAX_BYTES = 5 * 1024 * 1024;

const UX_COPY =
  "Upload CSV to create matches in bulk. Required: league, status, startAt. For team matches, provide home/away team names. For country matches, provide homeCountry/awayCountry as World Cup nation shortName (e.g. ENG, MEX, USA) or ISO-2 for AFCON.";

const SAMPLE_CSV =
  "league,status,startAt,type,round,home,away,homeCountry,awayCountry,homeScore,awayScore\n" +
  "Premier League,upcoming,2025-12-19T18:00:00Z,teams,,Everton,Chelsea,,,0,0\n" +
  "Africa Cup of Nations,upcoming,2025-12-21T20:00:00Z,countries,Group stage,,,MA,KM,0,0\n" +
  "FIFA World Cup,upcoming,2026-06-11T00:00:00Z,countries,Group A,,,MEX,USA,0,0\n";

const downloadText = (filename, content, mime = "text/plain;charset=utf-8") => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const escapeCsv = (value) => {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[",\n]/.test(s)) {
    return `"${s.replaceAll('"', '""')}"`;
  }
  return s;
};

const formatErrorsCsv = (errors) => {
  const header = "row,message\n";
  const rows = (errors || [])
    .map((e) => `${escapeCsv(e.row)},${escapeCsv(e.message)}`)
    .join("\n");
  return header + rows + (rows ? "\n" : "");
};

const formatErrorsText = (errors) =>
  (errors || []).map((e) => `Row ${e.row}: ${e.message}`).join("\n");

export const MatchImportDialog = ({ open, onClose }) => {
  const [file, setFile] = useState(null);
  const [localError, setLocalError] = useState(null);
  const queryClient = useQueryClient();

  const { mutate, data, error, isPending, reset } = useMutation({
    mutationKey: ["importMatchesCsv"],
    mutationFn: () => importMatchesCsv(file),
    onSuccess: (result) => {
      queryClient.invalidateQueries(["matches"]);

      const created = result?.created ?? 0;
      const failed = result?.failed ?? 0;
      if (created > 0 && failed === 0) {
        handleClose();
      }
    },
  });

  const errors = useMemo(
    () => (data?.errors && Array.isArray(data.errors) ? data.errors : []),
    [data],
  );

  const onPickFile = (picked) => {
    reset();
    setLocalError(null);
    setFile(null);

    if (!picked) {
      return;
    }
    if (picked.size > MAX_BYTES) {
      setLocalError("File is too large. Max size is 5 MB.");
      return;
    }
    setFile(picked);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setLocalError(null);
    reset();
    if (!file) {
      setLocalError("Please choose a CSV file.");
      return;
    }
    mutate();
  };

  const onCopyErrors = async () => {
    const text = formatErrorsText(errors);
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied errors to clipboard");
    } catch {
      downloadText("match-import-errors.txt", text);
    }
  };

  const handleClose = () => {
    reset();
    setLocalError(null);
    setFile(null);
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import matches (CSV)</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} component="form" onSubmit={onSubmit}>
          <Alert severity="info">{UX_COPY}</Alert>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              CSV file (max 5 MB)
            </Typography>

            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => onPickFile(e.target.files?.[0])}
              disabled={isPending}
            />

            {file && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: <b>{file.name}</b> ({Math.ceil(file.size / 1024)} KB)
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              type="submit"
              variant="contained"
              startIcon={<UploadFileIcon />}
              disabled={isPending}
            >
              Upload & Import
            </Button>

            <Button
              type="button"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() =>
                downloadText(
                  "match-import-sample.csv",
                  SAMPLE_CSV,
                  "text/csv;charset=utf-8",
                )
              }
              disabled={isPending}
            >
              Download sample CSV
            </Button>
          </Stack>

          {(localError || error) && (
            <Alert severity="error">{localError || String(error)}</Alert>
          )}

          {data && (
            <Alert severity={errors.length ? "warning" : "success"}>
              Result: created <b>{data.created ?? 0}</b>, failed{" "}
              <b>{data.failed ?? 0}</b>
              {typeof data.message === "string" ? ` — ${data.message}` : ""}
            </Alert>
          )}

          {errors.length > 0 && (
            <>
              <Divider />

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                alignItems="center"
              >
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Errors
                </Typography>

                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={onCopyErrors}
                >
                  Copy errors
                </Button>

                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() =>
                    downloadText(
                      "match-import-errors.csv",
                      formatErrorsCsv(errors),
                      "text/csv;charset=utf-8",
                    )
                  }
                >
                  Download errors
                </Button>
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 120 }}>
                      <b>Row</b>
                    </TableCell>

                    <TableCell>
                      <b>Message</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {errors.map((e, idx) => (
                    <TableRow key={`${e.row}-${idx}`}>
                      <TableCell>{e.row}</TableCell>

                      <TableCell>{e.message}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
